import {
    CONFERENCE_JOINED,
    KICKED_OUT,
    getCurrentConference
} from '../base/conference';
import { disconnect } from '../base/connection';
import { hideDialog, isDialogOpen } from '../base/dialog';
import { MEDIA_TYPE } from '../base/media';
import { setActiveModalId } from '../base/modal';
import { getLocalParticipant, pinParticipant } from '../base/participants';
import { MiddlewareRegistry, StateListenerRegistry } from '../base/redux';
import { SET_REDUCED_UI } from '../base/responsive-ui';
import { isLocalTrackMuted } from '../base/tracks';
import { checkIfReactNative } from '../base/util/checkOS';
import { isInBreakoutRoom } from '../breakout-rooms/functions';
import { OPEN_CHAT } from '../chat/actionTypes';
import { closeChat } from '../chat/actions.any';
import { IS_ENABLED } from '../clickandpledge-givent/actionTypes';
import { isClickandPledge } from '../clickandpledge-givent/actions';
import { FeedbackDialog } from '../feedback';
import { setFilmstripEnabled } from '../filmstrip';
import { PARTICIPANTS_PANE_OPEN } from '../participants-pane/actionTypes';
import { close as closeParticipantsPane } from '../participants-pane/actions';
import { setToolboxEnabled } from '../toolbox/actions';
import { muteLocal } from '../video-menu/actions.any';

import { MEETING_LIMIT_EXCEEDED_COMMAND, SET_MEETING_LIMIT_EXCEEDED } from './actionTypes';
import { notifyKickedOut, setMeetingLimitExceeded, verifyConferenceMeetingLimit } from './actions';
import { hangUpReactNative } from './functions';

let meetingLimitWatchdogInterval = null;

/**
 * The local audio/video mute state as it was immediately before the meeting limit paused the call, or null when
 * the call is not paused.
 *
 * Non-null is what marks the call as *currently paused by the limit*: the redux flag cannot be used for that here
 * because this middleware runs after `next(action)`, so by the time a SET_MEETING_LIMIT_EXCEEDED case is reached
 * `meetingLimitExceeded` already holds the new value and the previous one is gone. It also has to be cleared on
 * CONFERENCE_JOINED, because `_conferenceWillLeave` resets `meetingLimitExceeded` in the reducer directly, without
 * dispatching SET_MEETING_LIMIT_EXCEEDED - so on a room switch this is the only copy of the pause state left.
 *
 * @type {{ audio: boolean, video: boolean }|null}
 */
let preLimitMuteState = null;

/**
 * Puts local media back the way it was before the meeting limit paused the call.
 *
 * Gives back exactly what the limit took away and nothing more: a participant who was already muted before the
 * pause stays muted, and unpauses themselves with the toolbar that has just been re-enabled.
 *
 * @param {Object} store - Redux store.
 * @param {{ audio: boolean, video: boolean }} restoreTo - The mute state captured when the pause began.
 * @returns {void}
 */
function _restoreMediaAfterMeetingLimit({ dispatch }, restoreTo) {
    // `muteLocal` unmutes a track that is still there, but it cannot re-create one the pause removed: the tracks
    // middleware only creates a track on unmute while the prejoin screen is up, so on web conference.js has to
    // re-run gUM. When the track did survive, the redux state has already flipped by then and conference.js's own
    // `isLocalAudioMuted() === mute` check turns the second call into a no-op, so this cannot double up on gUM.
    const resume = mediaType => {
        dispatch(muteLocal(false, mediaType));

        if (typeof APP === 'undefined' || !APP.conference) {
            return;
        }

        try {
            mediaType === MEDIA_TYPE.AUDIO
                ? APP.conference.muteAudio(false)
                : APP.conference.muteVideo(false);
        } catch {
            // Ignore
        }
    };

    restoreTo.audio || resume(MEDIA_TYPE.AUDIO);
    restoreTo.video || resume(MEDIA_TYPE.VIDEO);
}

/**
 * Starts the watchdog to enforce muting of local tracks while meeting limit is exceeded.
 *
 * @param {Object} store - Redux store.
 * @returns {void}
 */
function _startMeetingLimitWatchdog(store) {
    if (meetingLimitWatchdogInterval) {
        clearInterval(meetingLimitWatchdogInterval);
    }

    const enforceMute = () => {
        const state = store.getState();

        if (!state['features/base/conference']?.meetingLimitExceeded) {
            _stopMeetingLimitWatchdog();

            return;
        }

        const tracks = state['features/base/tracks'] || [];

        // If local tracks are unmuted, re-mute them via Redux
        if (!isLocalTrackMuted(tracks, MEDIA_TYPE.AUDIO)) {
            store.dispatch(muteLocal(true, MEDIA_TYPE.AUDIO));
        }
        if (!isLocalTrackMuted(tracks, MEDIA_TYPE.VIDEO)) {
            store.dispatch(muteLocal(true, MEDIA_TYPE.VIDEO));
        }
    };

    enforceMute();
    meetingLimitWatchdogInterval = setInterval(enforceMute, 1000);
}

/**
 * Stops the meeting limit watchdog timer.
 *
 * @returns {void}
 */
function _stopMeetingLimitWatchdog() {
    if (meetingLimitWatchdogInterval) {
        clearInterval(meetingLimitWatchdogInterval);
        meetingLimitWatchdogInterval = null;
    }
}

MiddlewareRegistry.register(store => next => action => {
    const result = next(action);

    switch (action.type) {
    case OPEN_CHAT: {
        const { dispatch } = store;

        dispatch(closeParticipantsPane());
        dispatch(isClickandPledge(false));
        break;
    }

    case PARTICIPANTS_PANE_OPEN: {
        const { dispatch } = store;

        dispatch(closeChat());
        dispatch(isClickandPledge(false));
        break;
    }

    case IS_ENABLED: {
        if (action.payload === true) {
            const { dispatch } = store;

            dispatch(closeChat());
            dispatch(closeParticipantsPane());
        }
        break;
    }

    case CONFERENCE_JOINED: {
        const { dispatch, getState } = store;
        const state = getState();
        const { reducedUI } = state['features/base/responsive-ui'];

        dispatch(setToolboxEnabled(!reducedUI));
        dispatch(setFilmstripEnabled(!reducedUI));

        // Any pause recorded here belongs to the conference that was just left (moving between the main room and a
        // breakout room re-joins), and `_conferenceWillLeave` has already cleared the redux flag for it. Leaving it
        // set would make the first not-exceeded result in the new room look like a recovery.
        //
        // Media is deliberately left as it is: a participant a moderator sends into a breakout room while the
        // call is paused arrives muted and unmutes themselves with the toolbar the two dispatches above have
        // just re-enabled. What they had before the pause is not recoverable from this side of the room switch,
        // and guessing is what caused the bug this guard exists for.
        preLimitMuteState = null;

        // Verify meeting limit inside active conference
        setTimeout(() => {
            if (getCurrentConference(getState())) {
                dispatch(verifyConferenceMeetingLimit());
            }
        }, 1500);

        break;
    }

    case SET_MEETING_LIMIT_EXCEEDED: {
        const { dispatch, getState } = store;

        if (action.isExceeded) {
            // Only the transition into the pause records what to come back to. This action is re-dispatched while
            // already paused - the moderator re-broadcasts it over presence, and every re-verification dispatches
            // its result unconditionally - and by then the watchdog has muted both tracks, so capturing again
            // would overwrite the user's real preference with the pause's own doing.
            if (!preLimitMuteState) {
                const tracks = getState()['features/base/tracks'];

                // `isLocalTrackMuted` reports an absent track as muted, which is the wanted answer: joining
                // muted from the prejoin screen leaves no track at all, and that is still "off by choice".
                preLimitMuteState = {
                    audio: isLocalTrackMuted(tracks, MEDIA_TYPE.AUDIO),
                    video: isLocalTrackMuted(tracks, MEDIA_TYPE.VIDEO)
                };
            }

            dispatch(muteLocal(true, MEDIA_TYPE.AUDIO));
            dispatch(muteLocal(true, MEDIA_TYPE.VIDEO));
            dispatch(setToolboxEnabled(false));
            dispatch(setFilmstripEnabled(false));
            _startMeetingLimitWatchdog(store);
        } else {
            _stopMeetingLimitWatchdog();
            dispatch(setToolboxEnabled(true));
            dispatch(setFilmstripEnabled(true));

            // Restore media only when coming back from a pause this session actually applied.
            //
            // `verifyConferenceMeetingLimit` dispatches its result unconditionally, including the not-exceeded
            // result of the check that runs 1.5s after every CONFERENCE_JOINED. Unmuting unconditionally here
            // therefore ran on every ordinary join and switched the camera and microphone back on a second and a
            // half after the user joined - discarding the choice they had just made on the prejoin screen. For a
            // moderator it did not merely flip the redux flag either: `muteAudio(false)`/`muteVideo(false)` take
            // the create-a-new-track branch for moderators, so the hardware was actually re-opened.
            const restoreTo = preLimitMuteState;

            preLimitMuteState = null;

            restoreTo && _restoreMediaAfterMeetingLimit(store, restoreTo);

            // Trigger VideoLayout update so LargeVideo and thumbnails re-render live stream
            restoreTo && setTimeout(() => {
                if (typeof APP !== 'undefined' && APP.UI) {
                    try {
                        const localId = getLocalParticipant(getState())?.id;
                        const VideoLayout = require('../../../modules/UI/videolayout/VideoLayout').default;

                        if (VideoLayout) {
                            if (localId) {
                                VideoLayout.updateLargeVideo(localId, true);
                            }
                            VideoLayout.refreshLayout();
                            VideoLayout.updateAllVideos();
                        }
                    } catch {
                        // Ignore
                    }
                }
            }, 250);
        }
        break;
    }

    case SET_REDUCED_UI: {
        const { dispatch, getState } = store;
        const state = getState();
        const { reducedUI } = state['features/base/responsive-ui'];

        dispatch(setToolboxEnabled(!reducedUI));
        dispatch(setFilmstripEnabled(!reducedUI));

        break;
    }

    case KICKED_OUT: {

        const { dispatch, getState } = store;
        let reason = action.reason;
        let hasExited = false;

        if (isInBreakoutRoom(getState())) {
            reason = 'dialog.hangUpLeaveReason';
        }

        const exitCall = () => {
            if (hasExited) {
                return;
            }
            hasExited = true;

            if (checkIfReactNative()) {
                dispatch(hangUpReactNative());

                return;
            }

            // Use the web hangup flow for complete teardown and navigation.
            dispatch(disconnect(false));
        };

        dispatch(notifyKickedOut(
            action.participant,
            exitCall,
            reason
        ));

        // On web we should terminate immediately after kick so the user does not
        // remain on a reconnecting state.
        if (!checkIfReactNative()) {
            exitCall();
        }

        break;
    }
    }

    return result;
});

/**
 * Set up state change listener to perform maintenance tasks when the conference
 * is left or failed, close all dialogs and unpin any pinned participants.
 */
StateListenerRegistry.register(
    state => getCurrentConference(state),
    (conference, store, prevConference) => {
        const { dispatch, getState } = store;
        const { authRequired, membersOnly, passwordRequired }
            = getState()['features/base/conference'];

        if (prevConference) {
            try {
                prevConference.removeCommandListener(MEETING_LIMIT_EXCEEDED_COMMAND);
            } catch {
                // Ignore cleanup error
            }
        }

        if (conference !== prevConference) {
            _stopMeetingLimitWatchdog();

            // Unpin participant, in order to avoid the local participant
            // remaining pinned, since it's not destroyed across runs.
            dispatch(pinParticipant(null));

            // XXX I wonder if there is a better way to do this. At this stage
            // we do know what dialogs we want to keep but the list of those
            // we want to hide is a lot longer. Thus we take a bit of a shortcut
            // and explicitly check.
            if (typeof authRequired === 'undefined'
                    && typeof passwordRequired === 'undefined'
                    && typeof membersOnly === 'undefined'
                    && !isDialogOpen(getState(), FeedbackDialog)) {
                // Conference changed, left or failed... and there is no
                // pending authentication, nor feedback request, so close any
                // dialog we might have open.
                dispatch(hideDialog());
            }

            // We want to close all modals.
            dispatch(setActiveModalId());
        }

        if (conference && conference !== prevConference) {
            conference.addCommandListener(
                MEETING_LIMIT_EXCEEDED_COMMAND,
                ({ attributes }) => {
                    if (attributes) {
                        const isPaused = attributes.paused === 'true' || attributes.paused === true;
                        let details = {};

                        try {
                            details = attributes.details ? JSON.parse(attributes.details) : {};
                        } catch {
                            details = {};
                        }

                        const currentLimitExceeded = getState()['features/base/conference']?.meetingLimitExceeded;

                        if (currentLimitExceeded !== isPaused) {
                            dispatch(setMeetingLimitExceeded(isPaused, details));
                        }
                    }
                }
            );
        }
    });
